import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { Dialog, DialogCancelButton } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import { Spinner } from "@/components/ui/Spinner";
import { LockedAutocomplete } from "@/components/LockedAutocomplete";
import type { AutocompleteOption } from "@/components/LockedAutocomplete";
import { useCurrentUser, userIsBuero } from "@/lib/auth";
import { ApiError } from "@/lib/apiClient";
import { PrintJobFeedbackError } from "@/lib/printJob";
import { logCoverPrint } from "@/lib/coverPrintLog";
import { toast } from "@/lib/toast";
import { LEITWEG_ID_HELP } from "@/lib/leitwegId";
import {
  createOrder,
  getCoverPrintUrl,
  getDefaultPrintLocation,
  getNextNummer,
  getNextNummerS,
  getPrintLocations,
  getTagSuggestions,
  listOrders,
  listOrdner,
  listSOrdner,
  runPrintByLocationWithFeedback,
  runBeschreibungAssist,
  setOrderTags,
} from "@/lib/projects";
import type {
  BeschreibungAssistResponse,
  CreateOrderPayload,
  CreatedOrder,
  HeroAddressLookup,
  HeroContactLookup,
  HeroCustomerLookup,
  OrdnerOut,
  PrintLocation,
  SOrdnerOut,
} from "@/lib/projects";
import {
  addressSource,
  assetSource,
  contactSource,
  customerSource,
  ordnerSource,
  resolveGeschaeftspartnerId,
  sordnerSource,
  type WartungsobjektOption,
} from "@/pages/projekte/sources";
import type {
  NewProjectFormPrefill,
  NewProjectOrdnerPrefill,
} from "@/pages/projekte/newProjectContextValue";
import { uploadFilesToAuftraege } from "@/features/globalUpload/globalUploadApi";
import type { GlobalUploadFileEntry } from "@/features/globalUpload/globalUploadConstants";
import { resolveGpBestellnummer } from "@/lib/kontakte";
import { BestellnummerField } from "@/components/BestellnummerField";
import { ObjektadresseFormDialog } from "@/pages/kontakte/ObjektadresseFormDialog";

/* ─────────────── Typen ─────────────── */

export interface NewProjectDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: (order: CreatedOrder) => void;
  /** Aus Listenfilter: Ordner/S-Ordner vorauswählen und direkt Schritt 2 öffnen. */
  initialOrdnerPrefill?: NewProjectOrdnerPrefill | null;
  /** Formularfelder vorausfüllen (z. B. Tagesgeschäft-Mail-Analyse). */
  initialFormPrefill?: NewProjectFormPrefill | null;
  /** Unterdrückt die Ordner-Nachfrage „Wurde beauftragt?“ im Anlege-Dialog. */
  skipAskCommissionAfterCreate?: boolean;
}

type OrdnerType = "projekt" | "s";
type Step = 1 | 2;

interface ProjektOrdnerSelection {
  type: "projekt";
  ordner: OrdnerOut;
}

interface SOrdnerSelection {
  type: "s";
  ordner: SOrdnerOut;
}

type OrdnerSelection = ProjektOrdnerSelection | SOrdnerSelection;

interface FormState {
  kunde: AutocompleteOption<HeroCustomerLookup> | null;
  contact: AutocompleteOption<HeroContactLookup> | null;
  address: AutocompleteOption<HeroAddressLookup> | null;
  ortZusatz: string;
  assets: AutocompleteOption<WartungsobjektOption>[];
  beschreibung: string;
  bestellnummer: string;
  kundenreferenz: string;
  leitwegId: string;
  tags: string[];
  selectedKeywords: Set<string>;
  printCover: boolean;
  printLocationId: number | null;
  printCopies: number;
  manualNummer: string;
  /** null = noch nicht gewählt; nur relevant wenn Ordner-Flag gesetzt. */
  commissioned: boolean | null;
  /** Schadensmeldung / FM-Auftrag – optional, wenn Ordner-Flag gesetzt. */
  schadensmeldungFiles: File[];
}

const EMPTY_FORM: FormState = {
  kunde: null,
  contact: null,
  address: null,
  ortZusatz: "",
  assets: [],
  beschreibung: "",
  bestellnummer: "",
  kundenreferenz: "",
  leitwegId: "",
  tags: [],
  selectedKeywords: new Set(),
  printCover: false,
  printLocationId: null,
  printCopies: 1,
  manualNummer: "",
  commissioned: null,
  schadensmeldungFiles: [],
};

/* ─────────────── Helper ─────────────── */

function isAppAdmin(groups: string[] | undefined): boolean {
  return (groups || []).some((g) =>
    String(g)
      .toLowerCase()
      .replace(/[\s_]+/g, "-")
      .includes("app-admin"),
  );
}

function canPrintProjectCover(groups: string[] | undefined): boolean {
  const lower = (groups || []).map((g) => String(g).toLowerCase());
  return lower.some(
    (g) =>
      g.includes("buero") ||
      g.includes("büro") ||
      g.includes("montage") ||
      g.includes("monteur"),
  );
}

/**
 * Beschreibung "ordnerverträglich" machen: keine `\\/:*?"<>|`,
 * keine doppelten Leerzeichen, höchstens 200 Zeichen. Spiegelt grob das
 * Verhalten der klassischen App wider.
 */
function sanitizeBeschreibung(raw: string): string {
  return String(raw || "")
    .replace(/[\\/:*?"<>|]/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 200);
}

function ordnerNummerMatches(stored: string, needle: string): boolean {
  const a = stored.trim().toLowerCase();
  const b = needle.trim().toLowerCase();
  return !!a && !!b && (a === b || stored.trim() === needle.trim());
}

async function resolveOrdnerPrefill(
  prefill: NewProjectOrdnerPrefill,
): Promise<OrdnerSelection | null> {
  if (prefill.type === "s") {
    const list = await listSOrdner().catch(() => []);
    const hit = list.find((s) => ordnerNummerMatches(s.nummer, prefill.sordnerNummer));
    return hit ? { type: "s", ordner: hit } : null;
  }

  // Anlegen ist nur im laufenden Jahr erlaubt. Ein Filter auf ein
  // Vorjahr übernimmt nie dessen Ordner, sondern höchstens dieselbe
  // Ordnernummer im aktuellen Jahr.
  const jahr = new Date().getFullYear();
  const list = (await listOrdner(jahr).catch(() => [])).filter((o) => o.jahr === jahr);
  const matches = list.filter((o) => ordnerNummerMatches(o.nummer, prefill.ordnerNummer));
  const hit =
    (prefill.kunde
      ? matches.find((o) => o.kunden_name === prefill.kunde)
      : undefined) ?? matches[0];
  return hit ? { type: "projekt", ordner: hit } : null;
}

/* ─────────────── Komponente ─────────────── */

export function NewProjectDialog({
  open,
  onClose,
  onCreated,
  initialOrdnerPrefill = null,
  initialFormPrefill = null,
  skipAskCommissionAfterCreate = false,
}: NewProjectDialogProps) {
  const [step, setStep] = useState<Step>(1);
  const [type, setType] = useState<OrdnerType>("projekt");
  const [selection, setSelection] = useState<OrdnerSelection | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [printLocations, setPrintLocations] = useState<PrintLocation[]>([]);
  const [tagSuggestions, setTagSuggestions] = useState<string[]>([]);
  const [tagInput, setTagInput] = useState("");
  const [assistState, setAssistState] = useState<{
    loading: boolean;
    suggestion: string;
    reason: string;
    keywords: string[];
  }>({ loading: false, suggestion: "", reason: "", keywords: [] });
  const assistTimerRef = useRef<number | null>(null);
  const assistSeqRef = useRef(0);
  const lastAppliedOrdnerRef = useRef<string | null>(null);
  const lastAppliedFormPrefillRef = useRef<NewProjectFormPrefill | null>(null);
  const bestellnummerManualRef = useRef(false);
  const [bestellnummerRegelWert, setBestellnummerRegelWert] = useState<string | null>(null);
  /** True, solange Auto-Fill für den aktuell gewählten Ordner noch läuft. */
  const [prefilling, setPrefilling] = useState(false);

  // Vorschau auf die finale Auftragsnummer. Die tatsächliche Vergabe passiert
  // erst beim Speichern (s. handleSubmit), damit zwei parallele Anlagen nicht
  // dieselbe Nummer bekommen.
  const [nummerPreview, setNummerPreview] = useState<{
    ordnerNummer: string;
    nummer: number;
  } | null>(null);
  const [nummerPreviewLoading, setNummerPreviewLoading] = useState(false);

  const { user } = useCurrentUser();
  const groups = user?.groups as string[] | undefined;
  const userIsAdmin = useMemo(() => isAppAdmin(groups), [groups]);
  const userCanPrint = useMemo(() => canPrintProjectCover(groups), [groups]);
  const userCanCreateOrt = useMemo(() => userIsBuero(user), [user]);

  // Aktuelles Jahr ist fix – kein UI für Jahresauswahl.
  const currentYear = new Date().getFullYear();

  // Reset beim Öffnen.
  useEffect(() => {
    if (!open) return;
    setStep(1);
    setType("projekt");
    setSelection(null);
    // Wie in `auftraege.js` (siehe `userCanPrintProjectCover()`-Check beim
    // Öffnen des Dialogs): Haken bei „Deckblatt nach dem Anlegen direkt
    // drucken" ist vor-angekreuzt, sofern der User überhaupt drucken darf.
    setForm({ ...EMPTY_FORM, printCover: userCanPrint });
    setSubmitError(null);
    setValidationErrors([]);
    setTagInput("");
    setAssistState({ loading: false, suggestion: "", reason: "", keywords: [] });
    setNummerPreview(null);
    setNummerPreviewLoading(false);
    setPrefilling(false);
    lastAppliedOrdnerRef.current = null;
    lastAppliedFormPrefillRef.current = null;
    bestellnummerManualRef.current = false;
    setBestellnummerRegelWert(null);
  }, [open, userCanPrint]);

  // Aktiven Listenfilter übernehmen (wie `openNewModal` / `resolveOrdnerFromActiveFilters` in auftraege.js).
  useEffect(() => {
    if (!open || !initialOrdnerPrefill) return;
    let cancelled = false;
    void (async () => {
      const resolved = await resolveOrdnerPrefill(initialOrdnerPrefill);
      if (cancelled || !resolved) return;
      setType(resolved.type);
      setSelection(resolved);
      setStep(2);
    })();
    return () => {
      cancelled = true;
    };
  }, [open, initialOrdnerPrefill]);

  // Druck-Locations laden, wenn Dialog offen + Druckrechte vorhanden.
  useEffect(() => {
    if (!open || !userCanPrint) return;
    let cancelled = false;
    Promise.all([
      getPrintLocations().catch(() => []),
      getDefaultPrintLocation().catch(() => ({ default_location_id: null })),
    ]).then(([locations, def]) => {
      if (cancelled) return;
      setPrintLocations(locations || []);
      const defaultId = def?.default_location_id ?? null;
      setForm((prev) => ({
        ...prev,
        printLocationId:
          defaultId ??
          (locations && locations.length
            ? Number((locations[0] as PrintLocation).id ?? 0) || null
            : null),
      }));
    });
    return () => {
      cancelled = true;
    };
  }, [open, userCanPrint]);

  // Auto-Fill von Kunde und Ort, sobald ein Ordner gewählt ist.
  useEffect(() => {
    if (!open || !selection) return;
    const ordnerKey = `${selection.type}:${selection.ordner.id}`;
    if (lastAppliedOrdnerRef.current === ordnerKey) return;
    lastAppliedOrdnerRef.current = ordnerKey;

    let cancelled = false;
    setPrefilling(true);
    void (async () => {
      try {
        setForm((prev) => ({
          ...prev,
          kunde: null,
          contact: null,
          address: null,
          ortZusatz: "",
        }));

        const code: number | string | null = selection.ordner.kunden_code ?? null;
        const sOrdner = selection.type === "s" ? selection.ordner : null;
        const customers =
          code != null && String(code) !== ""
            ? await customerSource(code)("").catch(() => [])
            : [];
        if (cancelled) return;

        const gpId = sOrdner?.geschaeftspartner_id ?? null;
        const heroCustomerId = sOrdner?.hero_customer_id ?? null;
        const kunde =
          (gpId != null
            ? customers.find(
                (option) => Number(option.value.geschaeftspartner_id) === Number(gpId),
              )
            : null) ??
          (heroCustomerId != null
            ? customers.find((option) => Number(option.value.id) === Number(heroCustomerId))
            : null) ??
          (customers.length === 1 ? customers[0] : null);

        let address: AutocompleteOption<HeroAddressLookup> | null = null;
        const heroAddressId = sOrdner?.hero_address_id ?? null;
        const ortZusatz = String(sOrdner?.ort_name_text ?? "").trim();
        if (heroAddressId != null && kunde) {
          const fromApp = await addressSource(kunde.value)("").catch(() => []);
          if (cancelled) return;
          address =
            fromApp.find(
              (option) => Number(option.value.address_id) === Number(heroAddressId),
            ) ?? null;
          if (!address) {
            const title =
              String(sOrdner?.ort_name ?? "").trim() || `Adresse #${heroAddressId}`;
            address = {
              key: String(heroAddressId),
              label: title,
              value: {
                address_id: Number(heroAddressId),
                title,
              },
            };
          }
        }

        setForm((prev) => ({
          ...prev,
          kunde: kunde ?? prev.kunde,
          address: address ?? null,
          ortZusatz,
        }));
      } finally {
        if (!cancelled) setPrefilling(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, selection]);

  // Formular-Prefill (z. B. Tagesgeschäft): Textfelder nach Ordner-Autofill.
  useEffect(() => {
    if (!open || !initialFormPrefill) return;
    if (selection && prefilling) return;
    if (lastAppliedFormPrefillRef.current === initialFormPrefill) return;
    lastAppliedFormPrefillRef.current = initialFormPrefill;

    const beschreibung = String(initialFormPrefill.beschreibung || "").trim();
    const ortZusatzPrefill = String(initialFormPrefill.ortZusatz || "").trim();
    const adresseText = String(initialFormPrefill.adresse || "").trim();
    setForm((prev) => ({
      ...prev,
      beschreibung: beschreibung || prev.beschreibung,
      ortZusatz: ortZusatzPrefill || prev.ortZusatz || (adresseText && !prev.ortZusatz ? adresseText : prev.ortZusatz),
    }));
  }, [open, initialFormPrefill, selection, prefilling]);

  // Kontakt/Adresse aus Prefill matchen, sobald der Kunde bekannt ist.
  useEffect(() => {
    if (!open || !initialFormPrefill || prefilling) return;
    const kunde = form.kunde?.value ?? null;
    if (!kunde) return;

    const contactName = String(initialFormPrefill.ansprechpartner || "").trim();
    const adresseText = String(initialFormPrefill.adresse || "").trim();
    if (!contactName && !adresseText) return;

    let cancelled = false;
    void (async () => {
      if (contactName) {
        const contacts = await contactSource(kunde)("").catch(() => []);
        if (cancelled) return;
        const needle = contactName.toLowerCase();
        const match =
          contacts.find((option) => option.label.toLowerCase() === needle) ||
          contacts.find((option) => option.label.toLowerCase().includes(needle)) ||
          null;
        if (match) {
          setForm((prev) => ({ ...prev, contact: match }));
        }
      }
      if (adresseText) {
        const addresses = await addressSource(kunde)("").catch(() => []);
        if (cancelled) return;
        const needle = adresseText.toLowerCase();
        const match =
          addresses.find((option) => {
            const hay = [option.label, option.description, option.value.street, option.value.city]
              .filter(Boolean)
              .join(" ")
              .toLowerCase();
            return hay.includes(needle) || needle.includes((option.value.street || "").toLowerCase());
          }) || null;
        if (match) {
          setForm((prev) => ({ ...prev, address: match }));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, initialFormPrefill, prefilling, form.kunde]);

  // Vorschau der nächsten Auftragsnummer.  Triggert beim Auswählen des
  // Ordners in Schritt 1, damit die Nummer bereits sichtbar ist, sobald der
  // User in Schritt 2 wechselt.  Die finale Nummer wird beim Speichern
  // erneut allokiert.
  useEffect(() => {
    if (!open || !selection) {
      setNummerPreview(null);
      setNummerPreviewLoading(false);
      return;
    }
    let cancelled = false;
    setNummerPreviewLoading(true);
    setNummerPreview(null);
    void (async () => {
      try {
        const res =
          selection.type === "s"
            ? await getNextNummerS(selection.ordner.nummer)
            : await getNextNummer(
                selection.ordner.nummer,
                selection.ordner.jahr,
                selection.ordner.id,
              );
        if (cancelled) return;
        if (res?.naechste_nummer != null) {
          setNummerPreview({
            ordnerNummer: selection.ordner.nummer,
            nummer: res.naechste_nummer,
          });
        }
      } catch {
        /* still – Vorschau bleibt leer */
      } finally {
        if (!cancelled) setNummerPreviewLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selection]);

  // Tag-Vorschläge laden, sobald wir in Schritt 2 sind. Wir brauchen eine
  // Seed-Order-ID; den ersten verfügbaren Auftrag aus der Liste verwenden – fail-soft.
  useEffect(() => {
    if (step !== 2 || !open) return;
    let cancelled = false;
    (async () => {
      try {
        const list = await listOrders({ limit: 1 });
        const seed = list?.[0]?.id;
        if (!seed) return;
        const suggestions = await getTagSuggestions(seed);
        if (!cancelled) setTagSuggestions(suggestions);
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [step, open]);

  // Beschreibung-Assist debounced anstoßen.
  useEffect(() => {
    if (step !== 2) return;
    if (assistTimerRef.current) {
      window.clearTimeout(assistTimerRef.current);
      assistTimerRef.current = null;
    }
    const text = String(form.beschreibung || "").trim();
    if (text.length < 2) {
      setAssistState({
        loading: false,
        suggestion: "",
        reason: "",
        keywords: [],
      });
      return;
    }
    setAssistState((prev) => ({ ...prev, loading: true }));
    const seq = ++assistSeqRef.current;
    assistTimerRef.current = window.setTimeout(async () => {
      try {
        const data: BeschreibungAssistResponse = await runBeschreibungAssist(
          text,
        );
        if (seq !== assistSeqRef.current) return;
        setAssistState({
          loading: false,
          suggestion: String(data.suggested_beschreibung || "").trim(),
          reason: String(data.reason || "").trim(),
          keywords: Array.isArray(data.schlagworte)
            ? data.schlagworte.map((s) => String(s || "").trim()).filter(Boolean)
            : [],
        });
      } catch {
        if (seq !== assistSeqRef.current) return;
        setAssistState({
          loading: false,
          suggestion: "",
          reason: "",
          keywords: [],
        });
      }
    }, 650);
    return () => {
      if (assistTimerRef.current) {
        window.clearTimeout(assistTimerRef.current);
        assistTimerRef.current = null;
      }
    };
  }, [form.beschreibung, step]);

  // Cascading: Kunde geleert → Kontakt + Adresse leeren.
  useEffect(() => {
    if (form.kunde) return;
    if (form.contact || form.address || form.assets.length) {
      setForm((prev) => ({
        ...prev,
        contact: null,
        address: null,
        assets: [],
      }));
    }
  }, [form.kunde, form.contact, form.address, form.assets.length]);

  // Bestellnummer aus Kundenregeln, sobald Kunde + Ansprechpartner/Ort stehen.
  useEffect(() => {
    if (!open || step !== 2 || !form.kunde) {
      return;
    }
    let cancelled = false;
    const kunde = form.kunde.value;
    const apId = form.contact?.value.id ?? null;
    const heroAddressId = form.address?.value.address_id ?? null;
    void (async () => {
      const gpId = await resolveGeschaeftspartnerId(kunde);
      if (cancelled || gpId == null) {
        if (!cancelled) setBestellnummerRegelWert(null);
        return;
      }
      try {
        const resolved = await resolveGpBestellnummer(gpId, {
          ansprechpartner_id: apId,
          hero_address_id: heroAddressId,
        });
        if (cancelled) return;
        const next = String(resolved.bestellnummer || "").trim() || null;
        setBestellnummerRegelWert(next);
        if (!bestellnummerManualRef.current) {
          setForm((prev) => {
            const current = prev.bestellnummer.trim();
            if (current === (next || "")) return prev;
            return { ...prev, bestellnummer: next || "" };
          });
        }
      } catch {
        if (!cancelled) setBestellnummerRegelWert(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, step, form.kunde, form.contact, form.address]);

  /* ─────────── Validation ─────────── */

  const showSchadensmeldungUpload =
    selection?.type === "projekt" &&
    Boolean(selection.ordner.allow_schadensmeldung_on_create);

  const hasSchadensmeldungUpload = form.schadensmeldungFiles.length > 0;

  // Mit hochgeladener Schadensmeldung/FM entfällt die Beauftragungsfrage.
  const showAskCommission =
    !skipAskCommissionAfterCreate &&
    selection?.type === "projekt" &&
    Boolean(selection.ordner.ask_commission_after_create) &&
    !hasSchadensmeldungUpload;

  function collectMissing(): string[] {
    if (!selection) return ["Ordner"];
    const missing: string[] = [];
    if (!form.kunde) missing.push("Kunde");
    if (!form.contact) missing.push("Ansprechpartner");
    if (!form.address) missing.push("Ort");
    if (!sanitizeBeschreibung(form.beschreibung)) missing.push("Beschreibung");
    if (form.printCover && !form.printLocationId)
      missing.push("Druck-Standort");
    if (showAskCommission && form.commissioned === null)
      missing.push("Beauftragung");
    return missing;
  }

  /* ─────────── Submit ─────────── */

  async function handleSubmit() {
    if (!selection) return;
    setSubmitError(null);
    const missing = collectMissing();
    if (missing.length) {
      setValidationErrors(missing);
      return;
    }
    setValidationErrors([]);

    setSubmitting(true);
    try {
      if (selection.type === "projekt" && selection.ordner.jahr !== currentYear) {
        throw new Error(
          `Projekte können nur im Jahr ${currentYear} angelegt werden.`,
        );
      }
      // Auftragsnummer
      let finalNummer: number | null = null;
      const ordnerNummer = selection.ordner.nummer;
      const manualRaw = form.manualNummer.trim();
      if (selection.type === "s" && userIsAdmin && manualRaw) {
        const v = Number(manualRaw);
        if (!Number.isInteger(v) || v <= 0) {
          throw new Error("Manuelle Nummer muss eine ganze Zahl > 0 sein.");
        }
        finalNummer = v;
      }
      if (finalNummer == null) {
        const res =
          selection.type === "s"
            ? await getNextNummerS(ordnerNummer).catch(() => null)
            : await getNextNummer(
                ordnerNummer,
                selection.ordner.jahr,
                selection.ordner.id,
              ).catch(() => null);
        finalNummer = res?.naechste_nummer ?? null;
        if (finalNummer == null) {
          throw new Error("Nächste Auftragsnummer konnte nicht ermittelt werden.");
        }
      }

      // Tag-Liste zusammenstellen: manuell + ausgewählte KI-Vorschläge.
      const tagSet = new Set<string>();
      const tagsOut: string[] = [];
      const pushTag = (raw: string) => {
        const name = sanitizeBeschreibung(raw);
        if (!name) return;
        const k = name.toLowerCase();
        if (tagSet.has(k)) return;
        tagSet.add(k);
        tagsOut.push(name);
      };
      form.tags.forEach(pushTag);
      assistState.keywords.forEach((kw) => {
        if (form.selectedKeywords.has(kw.toLowerCase())) pushTag(kw);
      });

      const payload: CreateOrderPayload = {
        ordner_id: selection.type === "projekt" ? selection.ordner.id : null,
        sordner_id: selection.type === "s" ? selection.ordner.id : null,
        nummer: finalNummer,
        ansprechpartner_id: Number(form.contact!.value.id),
        ort_id: form.address?.value.ort_id != null
          ? Number(form.address.value.ort_id)
          : null,
        hero_address_id: form.address
          ? Number(form.address.value.address_id) || null
          : null,
        ort_name_text: form.address ? form.address.value.title : "",
        ort_zusatz: form.ortZusatz.trim() || null,
        beschreibung: sanitizeBeschreibung(form.beschreibung),
        bestellnummer: form.bestellnummer.trim() || null,
        kundenreferenz: form.kundenreferenz.trim() || null,
        leitweg_id: form.leitwegId.trim() || null,
        jahr: selection.type === "projekt" ? selection.ordner.jahr : currentYear,
        hero_customer_id: (() => {
          const heroId = Number(form.kunde?.value.id);
          return Number.isFinite(heroId) && heroId > 0 ? heroId : null;
        })(),
        geschaeftspartner_id: (() => {
          const gpId = Number(form.kunde?.value.geschaeftspartner_id);
          return Number.isFinite(gpId) && gpId > 0 ? gpId : null;
        })(),
        asset_ids: form.assets
          .map((opt) => Number(opt.value.asset_id))
          .filter((n) => Number.isFinite(n) && n > 0),
        print_cover_on_create: form.printCover && userCanPrint,
        print_location_id: form.printCover ? form.printLocationId : null,
        print_copies: Math.max(1, Number(form.printCopies) || 1),
      };

      const created = await createOrder(payload);

      // Schadensmeldung / FM-Auftrag: Upload setzt das Projekt direkt auf beauftragt.
      if (created?.id && hasSchadensmeldungUpload) {
        const uploadEntries = form.schadensmeldungFiles.map((file, fileIndex) => {
          const entry: GlobalUploadFileEntry = {
            id: `np-sm-${fileIndex}`,
            original: file,
            customName: file.name,
            hasConflict: false,
            asSchadensmeldung: true,
          };
          return { fileIndex, auftragId: created.id, entry };
        });
        const schadensmeldungen = {
          [created.id]: form.schadensmeldungFiles.map((_, file_index) => ({
            file_index,
          })),
        };
        try {
          await uploadFilesToAuftraege(uploadEntries, undefined, schadensmeldungen);
          window.dispatchEvent(new Event("auftraege_changed"));
          window.dispatchEvent(new Event("montageunterlagen_changed"));
          toast.success(
            form.schadensmeldungFiles.length === 1
              ? "Schadensmeldung / FM-Auftrag hochgeladen – Projekt ist beauftragt."
              : "Schadensmeldungen / FM-Aufträge hochgeladen – Projekt ist beauftragt.",
          );
        } catch (err) {
          console.warn(
            "[NewProjectDialog] Schadensmeldung-Upload fehlgeschlagen",
            err,
          );
          toast.error(
            err instanceof Error
              ? `Projekt angelegt, Upload fehlgeschlagen: ${err.message}`
              : "Projekt angelegt, Schadensmeldung konnte nicht hochgeladen werden.",
          );
        }
      }

      // Frage zur Beauftragung läuft bereits im Formular – bei „Ja“ öffnet
      // AskCommissionAfterCreateHost direkt den mündlichen Beauftragungs-Flow.
      // Entfällt, wenn bereits eine Schadensmeldung hochgeladen wurde.
      const createdWithAsk: CreatedOrder = {
        ...created,
        ask_commission_after_create:
          showAskCommission &&
          form.commissioned === true &&
          !hasSchadensmeldungUpload,
      };

      if (created?.id && tagsOut.length) {
        try {
          await setOrderTags(created.id, tagsOut);
        } catch (err) {
          console.warn("[NewProjectDialog] Tags konnten nicht gespeichert werden", err);
        }
      }

      // Modal sofort schließen; Deckblatt-Druck läuft im Hintergrund mit Toast-Feedback.
      onCreated?.(createdWithAsk);
      onClose();

      if (form.printCover && userCanPrint && form.printLocationId && created?.id) {
        const locationId = form.printLocationId;
        const copies = Math.max(1, Number(form.printCopies) || 1);
        const orderId = created.id;
        const selectedLocation = printLocations.find(
          (loc) => Number(loc.id ?? loc.key ?? loc.value) === locationId,
        );
        const locationName = selectedLocation?.name || selectedLocation?.label || null;
        void (async () => {
          try {
            const { url } = await getCoverPrintUrl(orderId);
            const job = await runPrintByLocationWithFeedback(locationId, copies, url, {
              loadingMessage: "Deckblatt wird gedruckt…",
            });
            await logCoverPrint(
              orderId,
              {
                cover_kind: "project",
                location_id: locationId,
                location_name: locationName,
                copies,
              },
              job,
            );
          } catch (err) {
            console.warn("[NewProjectDialog] Deckblatt-Druck fehlgeschlagen", err);
            // PrintJobFeedbackError zeigt bereits einen Fehler-Toast.
            if (!(err instanceof PrintJobFeedbackError)) {
              toast.error(
                err instanceof Error
                  ? `Deckblatt-Druck fehlgeschlagen: ${err.message}`
                  : "Deckblatt-Druck fehlgeschlagen.",
              );
            }
          }
        })();
      }
    } catch (err) {
      const message =
        err instanceof ApiError
          ? err.message
          : err instanceof Error
            ? err.message
            : "Projekt konnte nicht angelegt werden.";
      setSubmitError(message);
    } finally {
      setSubmitting(false);
    }
  }

  /* ─────────── Render ─────────── */

  const canContinueStep1 = !!selection;

  return (
    <Dialog
      open={open}
      onClose={submitting ? () => undefined : onClose}
      dirty={!!selection}
      title="Neues Projekt anlegen"
      description={
        step === 1
          ? "Zuerst den Ordner wählen."
          : "Kunde, Ort und Beschreibung reichen für die Anlage."
      }
      size="3xl"
      zIndexClass="z-[60]"
      bodyClassName="space-y-5"
      footer={
        step === 1 ? (
          <>
            <DialogCancelButton onClose={onClose} variant="ghost" />
            <Button onClick={() => setStep(2)} disabled={!canContinueStep1}>
              Weiter zu den Details
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="ghost"
              onClick={() => setStep(1)}
              disabled={submitting}
            >
              Zurück
            </Button>
            <Button onClick={handleSubmit} loading={submitting}>
              {hasSchadensmeldungUpload
                ? "Anlegen & Beauftragen"
                : showAskCommission && form.commissioned === true
                  ? "Anlegen & Beauftragung"
                  : "Projekt anlegen"}
            </Button>
          </>
        )
      }
    >
      <StepIndicator step={step} />
      {step === 1 ? (
        <Step1
          type={type}
          onTypeChange={(next) => {
            setType(next);
            setSelection(null);
            setForm((prev) => ({
              ...prev,
              commissioned: null,
              schadensmeldungFiles: [],
            }));
          }}
          selection={selection}
          onSelect={(next) => {
            setSelection(next);
            setForm((prev) => ({
              ...prev,
              commissioned: null,
              schadensmeldungFiles: [],
            }));
          }}
          currentYear={currentYear}
        />
      ) : (
        <Step2
          selection={selection}
          form={form}
          setForm={setForm}
          userIsAdmin={userIsAdmin}
          userCanPrint={userCanPrint}
          userCanCreateOrt={userCanCreateOrt}
          printLocations={printLocations}
          submitError={submitError}
          validationErrors={validationErrors}
          tagInput={tagInput}
          setTagInput={setTagInput}
          tagSuggestions={tagSuggestions}
          assistState={assistState}
          nummerPreview={nummerPreview}
          nummerPreviewLoading={nummerPreviewLoading}
          prefilling={prefilling}
          showAskCommission={showAskCommission}
          showSchadensmeldungUpload={showSchadensmeldungUpload}
          bestellnummerManualRef={bestellnummerManualRef}
          bestellnummerRegelWert={bestellnummerRegelWert}
        />
      )}
    </Dialog>
  );
}

/* ─────────────── Layout-Helfer ─────────────── */

function StepIndicator({ step }: { step: Step }) {
  const steps: { n: Step; label: string }[] = [
    { n: 1, label: "Ordner" },
    { n: 2, label: "Details" },
  ];
  return (
    <ol className="flex items-center gap-2" aria-label="Fortschritt">
      {steps.map((s, index) => {
        const done = step > s.n;
        const active = step === s.n;
        return (
          <li key={s.n} className="flex min-w-0 flex-1 items-center gap-2">
            <div
              className={clsx(
                "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold transition",
                done || active
                  ? "bg-accent text-label-on-accent"
                  : "bg-fill-tertiary text-label-tertiary",
                active && "ring-4 ring-accent-soft",
              )}
              aria-current={active ? "step" : undefined}
            >
              {done ? (
                <svg viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" aria-hidden>
                  <path
                    d="M3.5 8.5l3 3 6-7"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                s.n
              )}
            </div>
            <span
              className={clsx(
                "truncate text-sm font-medium",
                active || done ? "text-label" : "text-label-tertiary",
              )}
            >
              {s.label}
            </span>
            {index < steps.length - 1 ? (
              <div
                className={clsx(
                  "mx-1 h-px min-w-6 flex-1",
                  done ? "bg-accent/50" : "bg-separator",
                )}
                aria-hidden
              />
            ) : null}
          </li>
        );
      })}
    </ol>
  );
}

function FormSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-separator/80 bg-card px-4 py-4 shadow-sm sm:px-5">
      <header className="mb-3.5">
        <h3 className="text-sm font-semibold text-label">{title}</h3>
        {description ? (
          <p className="mt-0.5 text-xs leading-relaxed text-label-tertiary">{description}</p>
        ) : null}
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

/* ─────────────── Step 1 ─────────────── */

function Step1({
  type,
  onTypeChange,
  selection,
  onSelect,
  currentYear,
}: {
  type: OrdnerType;
  onTypeChange: (next: OrdnerType) => void;
  selection: OrdnerSelection | null;
  onSelect: (next: OrdnerSelection | null) => void;
  currentYear: number;
}) {
  const projektValue = useMemo<AutocompleteOption<OrdnerOut> | null>(() => {
    if (!selection || selection.type !== "projekt") return null;
    return {
      key: String(selection.ordner.id),
      label: `${selection.ordner.nummer} – ${selection.ordner.beschreibung || ""}`,
      description: `Kunde: ${selection.ordner.kunden_name} · Jahr ${selection.ordner.jahr}`,
      value: selection.ordner,
    };
  }, [selection]);

  const sValue = useMemo<AutocompleteOption<SOrdnerOut> | null>(() => {
    if (!selection || selection.type !== "s") return null;
    return {
      key: String(selection.ordner.id),
      label: selection.ordner.nummer,
      description: selection.ordner.beschreibung || undefined,
      value: selection.ordner,
    };
  }, [selection]);

  return (
    <div className="space-y-5">
      <FormSection
        title="Ordner wählen"
        description={
          type === "projekt"
            ? `Projekte werden im Jahr ${currentYear} angelegt.`
            : "S-Ordner für Service- und Sonderprojekte."
        }
      >
        <div>
          <span className="eh-label">Ordner-Typ</span>
          <div className="mt-1.5 flex gap-1 rounded-full bg-fill-tertiary p-1 text-sm">
            <ToggleButton active={type === "projekt"} onClick={() => onTypeChange("projekt")}>
              Projekt-Ordner
            </ToggleButton>
            <ToggleButton active={type === "s"} onClick={() => onTypeChange("s")}>
              S-Ordner
            </ToggleButton>
          </div>
        </div>

        {type === "projekt" ? (
          <LockedAutocomplete<OrdnerOut>
            label="Projekt-Ordner"
            required
            placeholder="Nummer, Beschreibung oder Kunde …"
            value={projektValue}
            loadOptions={(q) => ordnerSource(q, currentYear)}
            onChange={(opt) =>
              onSelect(opt ? { type: "projekt", ordner: opt.value } : null)
            }
          />
        ) : (
          <LockedAutocomplete<SOrdnerOut>
            label="S-Ordner"
            required
            placeholder="S-Nummer oder Beschreibung …"
            value={sValue}
            loadOptions={(q) => sordnerSource(q)}
            onChange={(opt) =>
              onSelect(opt ? { type: "s", ordner: opt.value } : null)
            }
          />
        )}
      </FormSection>

      {selection ? (
        <div className="rounded-2xl border border-status-green/25 bg-status-green/10 px-4 py-3.5">
          <div className="text-[11px] font-medium uppercase tracking-wide text-status-green">
            Ausgewählt
          </div>
          <div className="mt-1 font-mono text-sm font-semibold text-label">
            {selection.ordner.nummer}
            {selection.ordner.beschreibung
              ? ` – ${selection.ordner.beschreibung}`
              : ""}
          </div>
          {selection.type === "projekt" && selection.ordner.kunden_name ? (
            <div className="mt-1 text-xs text-label-secondary">
              {selection.ordner.kunden_name} · {selection.ordner.jahr}
              {selection.ordner.allow_schadensmeldung_on_create
                ? " · Schadensmeldung / FM-Auftrag möglich"
                : selection.ordner.ask_commission_after_create
                  ? " · Beauftragung wird im nächsten Schritt abgefragt"
                  : ""}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

/* ─────────────── Step 2 ─────────────── */

function Step2({
  selection,
  form,
  setForm,
  userIsAdmin,
  userCanPrint,
  userCanCreateOrt,
  printLocations,
  submitError,
  validationErrors,
  tagInput,
  setTagInput,
  tagSuggestions,
  assistState,
  nummerPreview,
  nummerPreviewLoading,
  prefilling,
  showAskCommission,
  showSchadensmeldungUpload,
  bestellnummerManualRef,
  bestellnummerRegelWert,
}: {
  selection: OrdnerSelection | null;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  userIsAdmin: boolean;
  userCanPrint: boolean;
  userCanCreateOrt: boolean;
  printLocations: PrintLocation[];
  submitError: string | null;
  validationErrors: string[];
  tagInput: string;
  setTagInput: (next: string) => void;
  tagSuggestions: string[];
  assistState: {
    loading: boolean;
    suggestion: string;
    reason: string;
    keywords: string[];
  };
  nummerPreview: { ordnerNummer: string; nummer: number } | null;
  nummerPreviewLoading: boolean;
  /** True, solange Auto-Fill für den aktuell gewählten Ordner noch läuft. */
  prefilling: boolean;
  showAskCommission: boolean;
  showSchadensmeldungUpload: boolean;
  bestellnummerManualRef: React.MutableRefObject<boolean>;
  bestellnummerRegelWert: string | null;
}) {
  // Wichtig: Hooks IMMER in derselben Reihenfolge aufrufen, daher hier oben –
  // der "no selection"-Branch wird erst danach gerendert.
  const missingSet = useMemo(
    () => new Set(validationErrors),
    [validationErrors],
  );

  const projektOrdner =
    selection?.type === "projekt" ? selection.ordner : null;
  const sOrdner = selection?.type === "s" ? selection.ordner : null;
  const kundenCode: number | string | null =
    projektOrdner?.kunden_code ?? sOrdner?.kunden_code ?? null;
  const customerLoad = useMemo(
    () => customerSource(kundenCode),
    [kundenCode],
  );
  const contactLoad = useMemo(
    () => contactSource(form.kunde?.value ?? null),
    [form.kunde],
  );
  const addressLoad = useMemo(
    () => addressSource(form.kunde?.value ?? null),
    [form.kunde],
  );
  const [ortCreateOpen, setOrtCreateOpen] = useState(false);
  const [ortCreateGpId, setOrtCreateGpId] = useState<number | null>(null);
  const [ortCreateResolving, setOrtCreateResolving] = useState(false);

  useEffect(() => {
    const kunde = form.kunde?.value ?? null;
    if (!userCanCreateOrt || !kunde) {
      setOrtCreateGpId(null);
      setOrtCreateResolving(false);
      setOrtCreateOpen(false);
      return;
    }
    let cancelled = false;
    setOrtCreateResolving(true);
    void resolveGeschaeftspartnerId(kunde)
      .then((id) => {
        if (!cancelled) setOrtCreateGpId(id);
      })
      .finally(() => {
        if (!cancelled) setOrtCreateResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [form.kunde, userCanCreateOrt]);

  if (!selection) {
    return (
      <p className="text-sm text-label-secondary">
        Bitte wechsle zurück zu Schritt 1 und wähle einen Ordner.
      </p>
    );
  }

  const ordnerHeading = `${selection.ordner.nummer}${
    selection.ordner.beschreibung ? ` – ${selection.ordner.beschreibung}` : ""
  }`;

  return (
    <div className="space-y-4">
      <div className="overflow-hidden rounded-2xl border border-separator/80 bg-card shadow-sm">
        <div className="flex flex-col sm:flex-row">
          <div className="min-w-0 flex-1 px-4 py-4 sm:px-5">
            <span className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
              {selection.type === "s" ? "S-Ordner" : "Projekt-Ordner"}
            </span>
            <div className="mt-1 truncate font-mono text-lg font-semibold tracking-tight text-label">
              {ordnerHeading}
            </div>
            {projektOrdner ? (
              <div className="mt-1 text-sm text-label-secondary">
                {projektOrdner.kunden_name} · {projektOrdner.jahr}
              </div>
            ) : (
              <div className="mt-1 text-sm text-label-secondary">
                Service- und Sonderprojekte
              </div>
            )}
          </div>

          <div className="flex flex-col justify-center gap-3 border-t border-separator/70 bg-fill-quaternary/40 px-4 py-4 sm:w-60 sm:border-l sm:border-t-0 sm:px-5">
            <NummerPreviewBadge
              ordnerNummer={selection.ordner.nummer}
              preview={nummerPreview}
              loading={nummerPreviewLoading}
              manualOverride={
                selection.type === "s" && userIsAdmin && form.manualNummer.trim()
                  ? Number(form.manualNummer)
                  : null
              }
            />
            {selection.type === "s" && userIsAdmin ? (
              <div>
                <label className="eh-label" htmlFor="np-manual-nummer">
                  Manuelle Nummer
                </label>
                <input
                  id="np-manual-nummer"
                  inputMode="numeric"
                  placeholder="auto"
                  className="eh-input"
                  value={form.manualNummer}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, manualNummer: e.target.value }))
                  }
                />
                <p className="mt-1 text-[11px] text-label-tertiary">
                  Leer lässt die nächste freie Nummer.
                </p>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {prefilling ? (
        <div className="flex items-center gap-2 rounded-2xl bg-fill-tertiary/60 px-3 py-2 text-xs text-label-secondary">
          <Spinner />
          <span>Kunde und Ort werden aus dem Ordner übernommen …</span>
        </div>
      ) : null}
      {validationErrors.length > 0 ? (
        <Alert variant="warning" title="Bitte fehlende Pflichtfelder ergänzen">
          {validationErrors.join(", ")}
        </Alert>
      ) : null}
      {submitError ? (
        <Alert variant="error" title="Fehler beim Speichern">
          {submitError}
        </Alert>
      ) : null}

      <FormSection
        title="Auftraggeber"
        description="Kunde und Ansprechpartner für dieses Projekt."
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <LockedAutocomplete<HeroCustomerLookup>
            label="Kunde"
            required
            placeholder={
              kundenCode == null
                ? "Am Ordner ist kein Kunde hinterlegt."
                : "Kunde suchen …"
            }
            value={form.kunde}
            loadOptions={customerLoad}
            onChange={(opt) => setForm((prev) => ({ ...prev, kunde: opt }))}
            disabled={!kundenCode}
            disabledHint={
              !kundenCode
                ? "Bitte den Ordner mit einem Kunden verknüpfen."
                : null
            }
            error={missingSet.has("Kunde") ? "Pflichtfeld" : null}
          />

          <LockedAutocomplete<HeroContactLookup>
            label="Ansprechpartner"
            required
            placeholder={
              form.kunde ? "Ansprechpartner suchen …" : "Bitte zuerst Kunde wählen"
            }
            value={form.contact}
            loadOptions={contactLoad}
            onChange={(opt) => setForm((prev) => ({ ...prev, contact: opt }))}
            disabled={!form.kunde}
            disabledHint={!form.kunde ? "Wählt zuerst den Kunden." : null}
            error={missingSet.has("Ansprechpartner") ? "Pflichtfeld" : null}
          />
        </div>
      </FormSection>

      <FormSection
        title="Einsatzort"
        description="Wo gearbeitet wird. Wartungsobjekte sind optional."
      >
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-2">
            <LockedAutocomplete<HeroAddressLookup>
              label="Ort"
              required
              placeholder={form.kunde ? "Ort suchen …" : "Bitte zuerst Kunde wählen"}
              value={form.address}
              loadOptions={addressLoad}
              onChange={(opt) =>
                setForm((prev) => ({
                  ...prev,
                  address: opt,
                  // Wartungsobjekte sind ortsgebunden – Auswahl zurücksetzen.
                  assets: [],
                }))
              }
              disabled={!form.kunde}
              disabledHint={!form.kunde ? "Wählt zuerst den Kunden." : null}
              error={missingSet.has("Ort") ? "Pflichtfeld" : null}
            />
            {userCanCreateOrt && form.kunde ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={ortCreateResolving || ortCreateGpId == null}
                onClick={() => setOrtCreateOpen(true)}
              >
                Ort anlegen
              </Button>
            ) : null}
          </div>

          <div>
            <label className="eh-label" htmlFor="np-ort-zusatz">
              Ort-Zusatz
            </label>
            <input
              id="np-ort-zusatz"
              type="text"
              placeholder='z. B. "Hinterhaus, 2. OG" …'
              className="eh-input"
              value={form.ortZusatz}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, ortZusatz: e.target.value }))
              }
            />
          </div>
        </div>

        <AssetsPicker
          heroAddressId={
            form.address ? Number(form.address.value.address_id) : null
          }
          value={form.assets}
          onChange={(next) => setForm((prev) => ({ ...prev, assets: next }))}
        />
      </FormSection>

      <FormSection
        title="Beschreibung"
        description="Kurztext für den Projektordner. Sonderzeichen werden automatisch entfernt."
      >
        <div className="space-y-2">
          <label className="eh-label" htmlFor="np-beschreibung">
            Beschreibung
            <span className="ml-1 text-status-red">*</span>
          </label>
          <textarea
            id="np-beschreibung"
            rows={3}
            placeholder="Kurzbeschreibung …"
            className={clsx(
              "eh-input",
              missingSet.has("Beschreibung") &&
                "border-status-red focus:border-status-red",
            )}
            value={form.beschreibung}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, beschreibung: e.target.value }))
            }
          />
          {assistState.loading ? (
            <div className="flex items-center gap-2 text-xs text-label-tertiary">
              <Spinner /> KI prüft die Beschreibung …
            </div>
          ) : null}
          {!assistState.loading &&
          (assistState.suggestion || assistState.reason) ? (
            <div className="rounded-ios bg-fill-tertiary/50 p-3 text-sm">
              <div className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
                KI-Vorschlag
              </div>
              {assistState.suggestion ? (
                <div className="mt-1 font-medium text-label">
                  {assistState.suggestion}
                </div>
              ) : null}
              {assistState.reason ? (
                <div className="mt-1 text-xs text-label-secondary">
                  {assistState.reason}
                </div>
              ) : null}
              {assistState.suggestion ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="mt-2"
                  onClick={() =>
                    setForm((prev) => ({
                      ...prev,
                      beschreibung: assistState.suggestion,
                    }))
                  }
                >
                  Vorschlag übernehmen
                </Button>
              ) : null}
            </div>
          ) : null}
          {assistState.keywords.length > 0 ? (
            <div className="space-y-1.5">
              <div className="text-xs text-label-secondary">
                Vorgeschlagene Schlagwörter
              </div>
              <div className="flex flex-wrap gap-1.5">
                {assistState.keywords.map((kw) => {
                  const lower = kw.toLowerCase();
                  const active = form.selectedKeywords.has(lower);
                  return (
                    <button
                      key={kw}
                      type="button"
                      onClick={() =>
                        setForm((prev) => {
                          const next = new Set(prev.selectedKeywords);
                          if (next.has(lower)) next.delete(lower);
                          else next.add(lower);
                          return { ...prev, selectedKeywords: next };
                        })
                      }
                      className={clsx(
                        "rounded-full border px-3 py-1 text-xs transition",
                        active
                          ? "border-accent bg-accent text-label-on-accent"
                          : "border-separator-opaque bg-fill-tertiary text-label hover:bg-fill",
                      )}
                    >
                      {kw}
                    </button>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </FormSection>

      <FormSection
        title="Referenzen"
        description="Optional. Die Bestellnummer kommt aus den Kundenregeln und steht danach auf den Dokumenten."
      >
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <BestellnummerField
            id="np-bestellnummer"
            value={form.bestellnummer}
            onChange={(next) => {
              bestellnummerManualRef.current =
                next.trim() !== (bestellnummerRegelWert || "");
              setForm((prev) => ({ ...prev, bestellnummer: next }));
            }}
            regelWert={bestellnummerRegelWert}
            onApplyRegel={() => {
              bestellnummerManualRef.current = false;
              setForm((prev) => ({
                ...prev,
                bestellnummer: bestellnummerRegelWert || "",
              }));
            }}
          />
          <div>
            <label className="eh-label" htmlFor="np-kundenreferenz">
              Kundenreferenz
            </label>
            <input
              id="np-kundenreferenz"
              type="text"
              className="eh-input"
              value={form.kundenreferenz}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, kundenreferenz: e.target.value }))
              }
              placeholder="Kundenreferenz …"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="eh-label" htmlFor="np-leitweg-id">
              Leitweg-ID
            </label>
            <input
              id="np-leitweg-id"
              type="text"
              className="eh-input font-mono"
              value={form.leitwegId}
              onChange={(e) => setForm((prev) => ({ ...prev, leitwegId: e.target.value }))}
              placeholder="z. B. 04011000-1234512345-06"
              spellCheck={false}
            />
            <p className="mt-1 text-xs text-label-tertiary">{LEITWEG_ID_HELP}</p>
          </div>
        </div>

        <TagsEditor
          tags={form.tags}
          onChange={(next) => setForm((prev) => ({ ...prev, tags: next }))}
          suggestions={tagSuggestions}
          input={tagInput}
          setInput={setTagInput}
        />
      </FormSection>

      {showAskCommission || userCanPrint || showSchadensmeldungUpload ? (
        <FormSection
          title="Nach dem Anlegen"
          description="Druck, Beauftragung und – falls am Ordner hinterlegt – Schadensmeldung / FM-Auftrag."
        >
          {showSchadensmeldungUpload ? (
            <SchadensmeldungFileDropzone
              files={form.schadensmeldungFiles}
              onAddFiles={(picked) => {
                if (!picked.length) return;
                setForm((prev) => ({
                  ...prev,
                  schadensmeldungFiles: [
                    ...prev.schadensmeldungFiles,
                    ...picked,
                  ],
                  commissioned: null,
                }));
              }}
              onRemoveFile={(index) =>
                setForm((prev) => ({
                  ...prev,
                  schadensmeldungFiles: prev.schadensmeldungFiles.filter(
                    (_, i) => i !== index,
                  ),
                }))
              }
            />
          ) : null}

          {showAskCommission ? (
            <div
              className={clsx(
                "rounded-ios px-3.5 py-3",
                missingSet.has("Beauftragung")
                  ? "bg-status-red/10 ring-1 ring-status-red/30"
                  : "bg-accent/5",
              )}
            >
              <div className="text-sm font-medium text-label">
                Projekt bereits beauftragt?
                <span className="ml-1 text-status-red">*</span>
              </div>
              <p className="mt-1 text-xs text-label-secondary">
                Bei „Ja“ öffnet sich nach dem Anlegen die mündliche Beauftragung
                (Notiz + Unterschrift).
              </p>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, commissioned: false }))
                  }
                  className={clsx(
                    "rounded-ios border px-3 py-2.5 text-sm font-medium transition",
                    form.commissioned === false
                      ? "border-accent bg-accent text-label-on-accent shadow-sm"
                      : "border-separator-opaque bg-card text-label hover:bg-fill-tertiary",
                  )}
                >
                  Nein
                </button>
                <button
                  type="button"
                  onClick={() =>
                    setForm((prev) => ({ ...prev, commissioned: true }))
                  }
                  className={clsx(
                    "rounded-ios border px-3 py-2.5 text-sm font-medium transition",
                    form.commissioned === true
                      ? "border-accent bg-accent text-label-on-accent shadow-sm"
                      : "border-separator-opaque bg-card text-label hover:bg-fill-tertiary",
                  )}
                >
                  Ja
                </button>
              </div>
            </div>
          ) : null}

          {userCanPrint ? (
            <div className="rounded-ios bg-fill-tertiary/40 px-3.5 py-3">
              <label className="flex items-start gap-2.5 text-sm">
                <input
                  type="checkbox"
                  className="mt-0.5 h-4 w-4 rounded border-separator-opaque text-accent focus:ring-accent-soft"
                  checked={form.printCover}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, printCover: e.target.checked }))
                  }
                />
                <span>
                  <span className="font-medium text-label">
                    Deckblatt direkt drucken
                  </span>
                  <span className="block text-xs text-label-secondary">
                    Druckt das Projekt-Deckblatt am gewählten Standort.
                  </span>
                </span>
              </label>
              {form.printCover ? (
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <label className="eh-label" htmlFor="np-print-location">
                      Standort
                      <span className="ml-1 text-status-red">*</span>
                    </label>
                    <select
                      id="np-print-location"
                      className={clsx(
                        "eh-input",
                        missingSet.has("Druck-Standort") &&
                          "border-status-red focus:border-status-red",
                      )}
                      value={
                        form.printLocationId ? String(form.printLocationId) : ""
                      }
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          printLocationId: e.target.value
                            ? Number(e.target.value)
                            : null,
                        }))
                      }
                    >
                      <option value="">— Standort auswählen —</option>
                      {printLocations.map((l) => {
                        const id = (l.id ?? l.key ?? l.value) as
                          | number
                          | string
                          | undefined;
                        const name = l.name ?? l.label ?? String(id ?? "");
                        if (id == null) return null;
                        return (
                          <option key={String(id)} value={String(id)}>
                            {name}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                  <div>
                    <label className="eh-label" htmlFor="np-print-copies">
                      Kopien
                    </label>
                    <input
                      id="np-print-copies"
                      type="number"
                      min={1}
                      step={1}
                      className="eh-input"
                      value={form.printCopies}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          printCopies: Math.max(1, Number(e.target.value) || 1),
                        }))
                      }
                    />
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </FormSection>
      ) : null}

      {userCanCreateOrt && ortCreateGpId != null ? (
        <ObjektadresseFormDialog
          open={ortCreateOpen}
          geschaeftspartnerId={ortCreateGpId}
          onClose={() => setOrtCreateOpen(false)}
          onSaved={(addr) => {
            setForm((prev) => ({
              ...prev,
              address: {
                key: addr.ort_id != null ? `ort-${addr.ort_id}` : `addr-${addr.address_id}`,
                label: addr.title,
                description:
                  [addr.street, addr.city].filter(Boolean).join(", ") || undefined,
                value: {
                  address_id: addr.address_id,
                  title: addr.title,
                  street: addr.street,
                  city: addr.city,
                  zipcode: addr.zipcode,
                  maps_link: addr.maps_link,
                  ort_id: addr.ort_id ?? null,
                },
              },
              assets: [],
            }));
          }}
        />
      ) : null}
    </div>
  );
}

/* ─────────────── Schadensmeldung Dropzone ─────────────── */

const SCHADENSMELDUNG_ACCEPT =
  ".pdf,.png,.jpg,.jpeg,.webp,.heic,.tif,.tiff,.doc,.docx,.msg,.eml";

function SchadensmeldungFileDropzone({
  files,
  onAddFiles,
  onRemoveFile,
}: {
  files: File[];
  onAddFiles: (files: File[]) => void;
  onRemoveFile: (index: number) => void;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const dragDepthRef = useRef(0);

  function handleFiles(list: FileList | File[] | null | undefined) {
    const picked = Array.from(list || []).filter((f) => f instanceof File);
    if (picked.length) onAddFiles(picked);
  }

  return (
    <div className="rounded-ios bg-accent/5 px-3.5 py-3">
      <div className="text-sm font-medium text-label">
        Schadensmeldung / FM-Auftrag
      </div>
      <p className="mt-1 text-xs text-label-secondary">
        Optional hochladen. Mit Datei entfällt die Beauftragungsfrage – das
        Projekt wird direkt als beauftragt gesetzt.
      </p>

      <input
        ref={inputRef}
        id="np-schadensmeldung-files"
        type="file"
        multiple
        accept={SCHADENSMELDUNG_ACCEPT}
        className="sr-only"
        onChange={(e) => {
          handleFiles(e.target.files);
          e.target.value = "";
        }}
      />

      <div
        role="button"
        tabIndex={0}
        aria-label="Schadensmeldung oder FM-Auftrag ablegen oder auswählen"
        className={clsx(
          "mt-3 flex min-h-[7.5rem] cursor-pointer flex-col items-center justify-center rounded-ios border border-dashed px-4 py-5 text-center transition",
          dragActive
            ? "border-accent bg-accent/15 ring-2 ring-accent-soft"
            : "border-separator/60 bg-fill-quaternary/30 hover:border-accent/50 hover:bg-accent/5",
        )}
        onClick={() => inputRef.current?.click()}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragEnter={(e) => {
          e.preventDefault();
          e.stopPropagation();
          dragDepthRef.current += 1;
          setDragActive(true);
        }}
        onDragOver={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (e.dataTransfer) e.dataTransfer.dropEffect = "copy";
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          e.stopPropagation();
          dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
          if (dragDepthRef.current === 0) setDragActive(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          dragDepthRef.current = 0;
          setDragActive(false);
          handleFiles(e.dataTransfer.files);
        }}
      >
        <svg
          viewBox="0 0 24 24"
          className="h-8 w-8 text-accent"
          fill="none"
          aria-hidden
        >
          <path
            d="M12 16V4m0 0l-3.5 3.5M12 4l3.5 3.5M4 16.5V18a2 2 0 002 2h12a2 2 0 002-2v-1.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <p className="mt-2 text-sm font-medium text-label">
          {dragActive
            ? "Dateien hier ablegen …"
            : "Dateien hierher ziehen oder auswählen"}
        </p>
        <p className="mt-1 text-xs text-label-tertiary">
          PDF, Bilder, Word, MSG/EML – mehrere Dateien möglich
        </p>
      </div>

      {files.length > 0 ? (
        <ul className="mt-2 space-y-1.5">
          {files.map((file, index) => (
            <li
              key={`${file.name}-${file.size}-${file.lastModified}-${index}`}
              className="flex items-center justify-between gap-2 rounded-ios bg-fill-tertiary/50 px-2.5 py-1.5 text-xs"
            >
              <span className="min-w-0 truncate text-label">{file.name}</span>
              <button
                type="button"
                className="shrink-0 rounded-full px-1.5 py-0.5 text-label-secondary hover:bg-fill-tertiary hover:text-label"
                aria-label={`„${file.name}" entfernen`}
                onClick={(e) => {
                  e.stopPropagation();
                  onRemoveFile(index);
                }}
              >
                Entfernen
              </button>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

/* ─────────────── Tags-Editor ─────────────── */

function TagsEditor({
  tags,
  onChange,
  suggestions,
  input,
  setInput,
}: {
  tags: string[];
  onChange: (next: string[]) => void;
  suggestions: string[];
  input: string;
  setInput: (next: string) => void;
}) {
  function addTag(raw: string) {
    const name = sanitizeBeschreibung(raw);
    if (!name) return;
    if (tags.some((t) => t.toLowerCase() === name.toLowerCase())) return;
    onChange([...tags, name]);
    setInput("");
  }

  const filteredSuggestions = useMemo(() => {
    const q = input.trim().toLowerCase();
    const used = new Set(tags.map((t) => t.toLowerCase()));
    const list = suggestions.filter((s) => !used.has(s.toLowerCase()));
    if (!q) return list.slice(0, 8);
    return list
      .filter((s) => s.toLowerCase().includes(q))
      .slice(0, 8);
  }, [suggestions, tags, input]);

  return (
    <section>
      <label className="eh-label" htmlFor="np-tag-input">
        Schlagwörter
      </label>
      <div className="flex gap-2">
        <input
          id="np-tag-input"
          type="text"
          className="eh-input flex-1"
          placeholder="Schlagwort eingeben und Enter drücken …"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              addTag(input);
            } else if (e.key === "Backspace" && !input && tags.length) {
              onChange(tags.slice(0, -1));
            }
          }}
        />
        <Button
          variant="secondary"
          onClick={() => addTag(input)}
          disabled={!input.trim()}
        >
          Hinzufügen
        </Button>
      </div>
      {tags.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {tags.map((t) => (
            <span
              key={t}
              className="inline-flex items-center gap-1 rounded-full border border-separator-opaque bg-fill-tertiary px-2.5 py-1 text-xs text-label"
            >
              {t}
              <button
                type="button"
                onClick={() =>
                  onChange(tags.filter((x) => x.toLowerCase() !== t.toLowerCase()))
                }
                className="rounded-full p-0.5 text-label-secondary hover:text-label"
                aria-label={`„${t}" entfernen`}
              >
                <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      {filteredSuggestions.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
          <span className="text-label-tertiary">Vorschläge:</span>
          {filteredSuggestions.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => addTag(s)}
              className="rounded-full border border-separator-opaque bg-card px-2 py-0.5 text-label hover:bg-fill-tertiary"
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

/* ─────────────── Wartungsobjekte-Picker ─────────────── */

function AssetsPicker({
  heroAddressId,
  value,
  onChange,
}: {
  heroAddressId: number | null;
  value: AutocompleteOption<WartungsobjektOption>[];
  onChange: (next: AutocompleteOption<WartungsobjektOption>[]) => void;
}) {
  const hasOrt = !!heroAddressId && heroAddressId > 0;
  const loader = useCallback(
    async (q: string) => {
      const list = await assetSource(heroAddressId)(q);
      const used = new Set(value.map((v) => v.key));
      return list.filter((opt) => !used.has(opt.key));
    },
    [heroAddressId, value],
  );

  return (
    <section>
      <span className="eh-label">Wartungsobjekte / Türen (optional)</span>
      {value.length > 0 && (
        <div className="mt-1 flex flex-wrap gap-1.5">
          {value.map((opt) => (
            <span
              key={opt.key}
              className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2.5 py-1 text-xs text-accent"
            >
              {opt.label}
              <button
                type="button"
                onClick={() => onChange(value.filter((v) => v.key !== opt.key))}
                className="rounded-full p-0.5 hover:bg-accent/15"
                aria-label={`„${opt.label}" entfernen`}
              >
                <svg viewBox="0 0 16 16" className="h-3 w-3" fill="none">
                  <path
                    d="M4 4l8 8M12 4l-8 8"
                    stroke="currentColor"
                    strokeWidth="1.6"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            </span>
          ))}
        </div>
      )}
      <div className="mt-2">
        <LockedAutocomplete<WartungsobjektOption>
          placeholder={
            hasOrt
              ? "Wartungsobjekt suchen … (kann mehrfach gewählt werden)"
              : "Bitte zuerst Ort wählen"
          }
          value={null}
          loadOptions={loader}
          keepOpenOnSelect
          reloadKey={`${heroAddressId ?? ""}:${value.map((v) => v.key).join(",")}`}
          onChange={(opt) => {
            if (!opt) return;
            if (value.some((v) => v.key === opt.key)) return;
            onChange([...value, opt]);
          }}
          disabled={!hasOrt}
          disabledHint={!hasOrt ? "Wählt zuerst den Ort." : null}
          hint="Nur Wartungsobjekte am gewählten Ort. Mehrere nacheinander wählen."
        />
      </div>
    </section>
  );
}

/* ─────────────── Projektnummer-Vorschau ─────────────── */

function NummerPreviewBadge({
  ordnerNummer,
  preview,
  loading,
  manualOverride,
}: {
  ordnerNummer: string;
  preview: { ordnerNummer: string; nummer: number } | null;
  loading: boolean;
  manualOverride: number | null;
}) {
  // Wenn der App-Admin auf einem S-Ordner eine manuelle Nummer eingegeben hat,
  // wird genau diese Nummer als endgültige verwendet – die Vorschau folgt der
  // Eingabe.
  let effective: { ordnerNummer: string; nummer: number } | null = preview;
  let isManual = false;
  if (manualOverride && manualOverride > 0) {
    effective = { ordnerNummer, nummer: manualOverride };
    isManual = true;
  }

  return (
    <div>
      <div className="text-[11px] font-medium uppercase tracking-wide text-label-tertiary">
        Projektnummer
      </div>
      <div className="mt-0.5 flex items-center gap-2 font-mono text-lg font-semibold tracking-tight text-label">
        {loading && !effective ? (
          <>
            <Spinner />
            <span className="text-label-tertiary">wird geladen …</span>
          </>
        ) : effective ? (
          <span>
            {effective.ordnerNummer}
            <span className="text-label-tertiary">#</span>
            {effective.nummer}
          </span>
        ) : (
          <span className="text-label-tertiary">—</span>
        )}
      </div>
      <p className="mt-1 text-[11px] leading-snug text-label-tertiary">
        {isManual
          ? "Diese Nummer wird beim Speichern verwendet."
          : "Vorschau. Die endgültige Nummer kommt beim Speichern."}
      </p>
    </div>
  );
}

/* ─────────────── Toggle-Button ─────────────── */

function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={clsx(
        "flex-1 rounded-full px-3 py-1.5 font-medium transition",
        active ? "bg-card text-label shadow-sm" : "text-label-secondary hover:text-label",
      )}
    >
      {children}
    </button>
  );
}
